import { useMemo, useState } from 'react'
import { ChevronDownIcon, Filter } from 'lucide-react'

import { buttonVariants } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  ACTIONS_BY_CATEGORY,
  ALL_ACTIONS,
  CATEGORY_ORDER,
  auditLogCategoryLabel,
  auditLogLabel,
  type AuditLogCategory,
} from '@/frontend/lib/auditLogs'
import { cn } from '@/lib/utils'

type CheckedState = boolean | 'indeterminate'

function useExclusionToggle(excludedActions: string[], onChange: (excluded: string[]) => void) {
  const excludedSet = new Set(excludedActions)
  const selectedCount = ALL_ACTIONS.length - excludedSet.size

  const toggleAction = (action: string) => {
    const next = new Set(excludedSet)
    if (next.has(action)) { next.delete(action) } else { next.add(action) }
    onChange(Array.from(next))
  }

  const toggleCategory = (category: AuditLogCategory) => {
    const actions = ACTIONS_BY_CATEGORY[category]
    const allExcluded = actions.every((a) => excludedSet.has(a))
    const next = new Set(excludedSet)
    actions.forEach((a) => allExcluded ? next.delete(a) : next.add(a))
    onChange(Array.from(next))
  }

  const toggleAll = () => onChange(excludedSet.size === 0 ? [...ALL_ACTIONS] : [])

  const allCheckedState: CheckedState = excludedSet.size === 0 ? true : excludedSet.size === ALL_ACTIONS.length ? false : 'indeterminate'

  const categoryCheckedState = (category: AuditLogCategory): CheckedState => {
    const actions = ACTIONS_BY_CATEGORY[category]
    const excludedCount = actions.filter((a) => excludedSet.has(a)).length
    if (excludedCount === 0) return true
    if (excludedCount === actions.length) return false
    return 'indeterminate'
  }

  return { excludedSet, selectedCount, toggleAction, toggleCategory, toggleAll, allCheckedState, categoryCheckedState }
}

interface AuditLogActionFilterProps {
  excludedActions: string[]
  onExcludedActionsChange: (excluded: string[]) => void
  className?: string
  workers?: Array<{ id: string; name: string }>
  digitalWorkerIds?: string[]
  onDigitalWorkerIdsChange?: (ids: string[]) => void
}

export function AuditLogActionFilter({ excludedActions, onExcludedActionsChange, className, workers, digitalWorkerIds, onDigitalWorkerIdsChange }: AuditLogActionFilterProps) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [workerSearch, setWorkerSearch] = useState('')
  const normalizedSearch = searchTerm.trim().toLowerCase()
  const normalizedWorkerSearch = workerSearch.trim().toLowerCase()

  const { excludedSet, selectedCount, toggleAction, toggleCategory, toggleAll, allCheckedState, categoryCheckedState } =
    useExclusionToggle(excludedActions, onExcludedActionsChange)

  const selectedWorkerIds = digitalWorkerIds ?? []
  const selectedWorkerSet = new Set(selectedWorkerIds)
  const hasWorkerFilter = selectedWorkerIds.length > 0
  const hasActiveFilters = selectedCount < ALL_ACTIONS.length || hasWorkerFilter
  const activeFilterCount = (selectedCount < ALL_ACTIONS.length ? 1 : 0) + (hasWorkerFilter ? 1 : 0)

  const filteredWorkers = useMemo(() =>
    normalizedWorkerSearch
      ? (workers ?? []).filter((w) => w.name.toLowerCase().includes(normalizedWorkerSearch))
      : (workers ?? []),
  [workers, normalizedWorkerSearch])

  const filteredCategories = useMemo(() =>
    CATEGORY_ORDER.map((category) => {
      const actions = ACTIONS_BY_CATEGORY[category]
      const filtered = normalizedSearch
        ? actions.filter((a) => auditLogLabel(a).toLowerCase().includes(normalizedSearch))
        : actions
      return { category, actions: filtered }
    }).filter((group) => group.actions.length > 0),
  [normalizedSearch])

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Filtros"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'default' }),
              'h-10 justify-between rounded-lg border-border bg-background px-3 py-2 text-sm font-normal text-foreground shadow-none hover:bg-background hover:text-foreground',
              'focus-visible:border-border focus-visible:ring-ring/70',
              open && 'border-border ring-2 ring-ring/60',
            )}
          >
            <span className="flex items-center gap-2">
              <Filter className="size-4 text-muted-foreground" />
              <span className={cn(!hasActiveFilters && 'text-muted-foreground')}>
                {hasActiveFilters ? `Filtros (${activeFilterCount})` : 'Filtros'}
              </span>
            </span>
            <ChevronDownIcon className={cn('size-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          sideOffset={4}
          className="w-80 rounded-lg border-border p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {workers && workers.length > 0 && onDigitalWorkerIdsChange && (
            <>
              <div className="border-b border-border p-3 pb-1.5 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Colaborador</span>
                  <span className="text-xs text-muted-foreground">
                    {selectedWorkerIds.length || workers.length} de {workers.length}
                  </span>
                </div>
                <Input
                  type="text"
                  value={workerSearch}
                  placeholder="Buscar colaborador..."
                  aria-label="Buscar colaborador"
                  onChange={(e) => setWorkerSearch(e.target.value)}
                  className="h-8 border-border text-sm shadow-none"
                />
              </div>
              <div className="border-b border-border">
                <ScrollArea className="overflow-hidden" style={{ height: `${Math.min(160, (filteredWorkers.length + 1) * 32 + 8)}px` }}>
                  <div className="p-2 space-y-1">
                    <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">
                      <Checkbox
                        checked={selectedWorkerIds.length === 0 ? true : 'indeterminate'}
                        onCheckedChange={() => onDigitalWorkerIdsChange([])}
                        aria-label="Todos os colaboradores"
                      />
                      <span className="font-medium text-foreground">Todos</span>
                      <span className="ml-auto text-xs text-muted-foreground">{workers.length}</span>
                    </label>
                    {filteredWorkers.map((worker) => (
                      <label
                        key={worker.id}
                        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedWorkerIds.length > 0 && selectedWorkerSet.has(worker.id)}
                          onCheckedChange={() => {
                            const next = new Set(selectedWorkerSet)
                            if (next.has(worker.id)) { next.delete(worker.id) } else { next.add(worker.id) }
                            onDigitalWorkerIdsChange(next.size >= workers.length ? [] : Array.from(next))
                          }}
                          aria-label={worker.name}
                        />
                        <span className="text-foreground truncate">{worker.name}</span>
                      </label>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </>
          )}

          <div className="border-b border-border p-3 pb-1.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</span>
              <span className="text-xs text-muted-foreground">{selectedCount} de {ALL_ACTIONS.length}</span>
            </div>
            <Input
              type="text"
              value={searchTerm}
              placeholder="Buscar ação..."
              aria-label="Buscar ação"
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 border-border text-sm shadow-none"
            />
          </div>

          <ScrollArea className="overflow-hidden" style={{ height: `${Math.min(300, filteredCategories.reduce((h, g) => h + 36 + g.actions.length * 32, 44))}px` }}>
            <div className="p-2 space-y-1">
              {!normalizedSearch && (
                <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer">
                  <Checkbox
                    checked={allCheckedState}
                    onCheckedChange={toggleAll}
                    aria-label="Selecionar todas as ações"
                  />
                  <span className="font-medium text-foreground">Selecionar todas</span>
                  <span className="ml-auto text-xs text-muted-foreground">{ALL_ACTIONS.length}</span>
                </label>
              )}

              {filteredCategories.map(({ category, actions }) => {
                const state = categoryCheckedState(category)

                return (
                  <div key={category}>
                    {!normalizedSearch && (
                      <label className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent cursor-pointer mt-1">
                        <Checkbox
                          checked={state}
                          onCheckedChange={() => toggleCategory(category)}
                          aria-label={`Selecionar todas as ações de ${auditLogCategoryLabel(category)}`}
                        />
                        <span className="font-medium text-foreground text-xs uppercase tracking-wider">
                          {auditLogCategoryLabel(category)}
                        </span>
                        <span className="ml-auto text-xs text-muted-foreground">
                          {ACTIONS_BY_CATEGORY[category].length}
                        </span>
                      </label>
                    )}

                    {actions.map((action) => (
                      <label
                        key={action}
                        className="flex items-center gap-2 rounded-md px-2 py-1 pl-6 text-sm hover:bg-accent cursor-pointer"
                      >
                        <Checkbox
                          checked={!excludedSet.has(action)}
                          onCheckedChange={() => toggleAction(action)}
                          aria-label={auditLogLabel(action)}
                        />
                        <span className="text-foreground truncate">{auditLogLabel(action)}</span>
                      </label>
                    ))}
                  </div>
                )
              })}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  )
}
